import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { Breadcrumbs } from '../components/Breadcrumbs';
export function CapabilitiesHubPage() {
  const processes = [{
    title: 'Research, Design and Development',
    image: 'https://images.unsplash.com/photo-1595590424283-b8f17842773f?w=800&q=80',
    copy: <>
          At Chiltern Global, every project begins with innovative research,
          design and product development to meet the exact requirements and
          requests of our clients. Our customer-focused and results driven team,
          who also hold experience within our markets, work closely with end
          users to define their requirements and performance objectives.
          <br data-id="element-382" />
          <br data-id="element-383" />
          This allows us to transition into prototyping and sampling, where we
          conduct hands-on evaluation and testing before our production takes
          place; ensuring accuracy, functionality and customer satisfaction from
          concept to completion.
        </>,
    highlights: ['Specification analysis', 'Prototyping & sampling', 'Hands-on evaluation & testing', 'Customer-focused development']
  }, {
    title: 'Production',
    image: 'https://images.unsplash.com/photo-1579912437766-7896df6d3cd3?w=800&q=80',
    copy: <>
          Once our clients approve the design, we advance into our production
          phase, and we understand that this is where precision and quality are
          paramount. Our manufacturing partners ensure they use state of the art
          materials, advanced fabric technologies and cost-efficient production
          methods to deliver consistent and high-quality results.
          <br data-id="element-384" />
          <br data-id="element-385" />
          At Chiltern Global, every stage of production incorporates stringent
          quality control and supervision which allows us to have full
          traceability, reliability and continuity throughout the manufacturing
          process; therefore, devoting Chiltern Global as a trusted supplier of
          defence, police and security equipment, globally.
        </>,
    highlights: ['State of the art materials', 'Advanced fabric technologies', 'Full traceability & reliability', 'Stringent quality control']
  }, {
    title: 'Quality Management',
    image: 'https://images.unsplash.com/photo-1580894894513-541e068a3e2b?w=800&q=80',
    copy: <>
          Chiltern Global operates under a multi-stage inspection process, from
          raw material sourcing to final assembly, ensuring compliance with{' '}
          <Link to="/about/certifications" className="text-cg-orange-600 hover:text-navy-900 font-bold transition-colors" data-id="element-386">
            ISO 14001
          </Link>{' '}
          and{' '}
          <Link to="/about/certifications" className="text-cg-orange-600 hover:text-navy-900 font-bold transition-colors" data-id="element-387">
            ISO 9001
          </Link>
          . With the ability to provide independent testing and certification,
          we guarantee that every product exceeds regulatory, environmental and
          ethical standards.
        </>,
    highlights: ['Multi-stage inspection', 'ISO 14001 & ISO 9001 compliance', 'Independent testing & certification', 'Regulatory & ethical standards']
  }, {
    title: 'Shipping and Logistics',
    image: 'https://images.unsplash.com/photo-1617791160505-6f00504e3519?w=800&q=80',
    copy: <>
          The final stage of our process focuses on efficient, secure and
          reliable logistics that ensure your equipment arrives on time and in
          excellent condition.
        </>,
    highlights: ['Efficient & secure delivery', 'Reliable logistics network', 'On-time fulfilment', 'Equipment condition assurance']
  }];
  const partners = [{
    name: 'Make UK Defence',
    logo: 'https://cdn.magicpatterns.com/uploads/cfP4ywzNizUEjpNhgJmApG/Make_UK_Defence_Logo_Col_72dpi_002_002.png',
    url: 'https://www.makeuk.org/about/make-uk-defence'
  }, {
    name: 'Armed Forces Covenant',
    logo: 'https://cdn.magicpatterns.com/uploads/placeholder/armed-forces-covenant.png'
  }, {
    name: 'Disability Confident',
    logo: 'https://cdn.magicpatterns.com/uploads/placeholder/disability-confident.png'
  }, {
    name: 'Cyber Essentials Plus',
    logo: 'https://cdn.magicpatterns.com/uploads/13vUPeUkJ8i5oDWP9yZJcD/cyberessentials_certification-mark-plus_colour.png'
  }, {
    name: 'JOSCAR',
    logo: 'https://cdn.magicpatterns.com/uploads/byihqp9gceccmuWddiSZfV/Joscar.png'
  }];
  return <>
      {/* 1. HERO BANNER */}
      <PageHero label="Manufacturing" title="Our Processes" subtitle="We manage our customers' projects from concept to completion to become their manufacturing partner of choice. From the initial tender, we manage the process the entire way until our customers receive the finished goods to specification, on time, and within budget every time." backgroundImage="https://cdn.magicpatterns.com/uploads/e7aUvLm1nf7EFY2JtRJTkW/Home_Page.jpg" data-id="element-388" />

      {/* 2. BREADCRUMBS */}
      <Breadcrumbs items={[{
      label: 'Manufacturing',
      to: '/manufacturing'
    }, {
      label: 'Our Processes'
    }]} data-id="element-389" />

      {/* 3. PROCESS SECTIONS — Markets-style alternating bands */}
      <section className="py-0" data-id="element-390">
        <div className="" data-id="element-391">
          {processes.map((process, index) => {
          const isEven = index % 2 === 0;
          const stepNumber = String(index + 1).padStart(2, '0');
          return <div key={index} id={process.title.toLowerCase().replace(/[\s,&]+/g, '-')} className={`scroll-mt-32 ${isEven ? 'bg-white' : 'bg-slate-100 border-y border-steel-200'}`} data-id="element-392">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20" data-id="element-393">
                  <div className="flex flex-col lg:flex-row gap-12" data-id="element-394">
                    {/* Left Panel — Image + Title (1/3) */}
                    <div className="lg:w-1/3" data-id="element-395">
                      <div className="aspect-video bg-navy-900 rounded-sm overflow-hidden mb-6 flex items-center justify-center p-8 relative group" data-id="element-396">
                        <img src={process.image} alt={process.title} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-50 transition-opacity" data-id="element-397" />
                        <div className="relative z-10 text-center" data-id="element-398">
                          <span className="text-cg-orange-600 font-bold text-sm uppercase tracking-widest block mb-2" data-id="element-399">
                            Step {stepNumber}
                          </span>
                          <h3 className="text-2xl font-bold text-white" data-id="element-400">
                            {process.title}
                          </h3>
                        </div>
                      </div>
                      <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-401">
                        {process.title}
                      </h2>
                      <p className="text-steel-600 mb-6 leading-relaxed text-sm" data-id="element-402">
                        Step {stepNumber} of our end-to-end manufacturing
                        process.
                      </p>
                      <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3 bg-navy-900 text-white font-bold uppercase tracking-wider text-sm rounded-sm hover:bg-cg-orange-600 transition-colors" data-id="element-403">
                        Discuss a Requirement
                      </Link>
                    </div>

                    {/* Right Panel — Content (2/3) */}
                    <div className="lg:w-2/3" data-id="element-404">
                      <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-6 flex items-center gap-2" data-id="element-405">
                        <span className="w-6 h-6 bg-cg-orange-600 text-white rounded-sm flex items-center justify-center text-xs font-bold" data-id="element-406">
                          {stepNumber}
                        </span>
                        Process Overview
                      </h3>

                      {/* Copy */}
                      <div className="text-lg text-steel-600 leading-relaxed mb-8" data-id="element-407">
                        {process.copy}
                      </div>

                      {/* Highlights Grid */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-408">
                        {process.highlights.map((highlight, i) => <div key={i} className={`flex items-start gap-3 p-4 rounded-sm border ${isEven ? 'bg-slate-50 border-steel-200' : 'bg-white border-steel-200'}`} data-id="element-409">
                            <ArrowRight className="w-4 h-4 text-cg-orange-600 flex-shrink-0 mt-0.5" data-id="element-410" />
                            <span className="text-sm font-medium text-navy-900" data-id="element-411">
                              {highlight}
                            </span>
                          </div>)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </section>

      {/* 4. CTA BLOCK */}
      <section className="bg-cg-orange-600 py-24" data-id="element-412">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center" data-id="element-413">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-414">
            Ready to Start your next project?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-10 text-lg leading-relaxed" data-id="element-415">
            Get in touch with our expert to discuss your manufacturing needs and
            discover how we can help bring your vision to life.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center px-10 py-4 bg-white text-cg-orange-600 font-bold uppercase tracking-widest rounded-sm hover:bg-navy-900 hover:text-white transition-colors" data-id="element-416">
            Get Started Today
          </Link>
        </div>
      </section>

      {/* 5. PARTNER LOGOS STRIP */}
      <section className="bg-white py-16 border-t border-steel-200" data-id="element-417">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-418">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 hover:opacity-100 transition-opacity duration-500" data-id="element-419">
            {partners.map((partner, index) => partner.url ? <a key={index} href={partner.url} target="_blank" rel="noopener noreferrer" className="block w-32 md:w-40 grayscale hover:grayscale-0 transition-all duration-300" data-id="element-420">
                  <img src={partner.logo} alt={partner.name} className="w-full h-auto object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.parentElement!.innerHTML = `<span class="text-sm font-bold text-navy-900 uppercase tracking-wider text-center block">${partner.name}</span>`;
            }} data-id="element-421" />
                </a> : <div key={index} className="block w-32 md:w-40 grayscale hover:grayscale-0 transition-all duration-300" data-id="element-422">
                  <img src={partner.logo} alt={partner.name} className="w-full h-auto object-contain" onError={e => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.parentElement!.innerHTML = `<span class="text-sm font-bold text-navy-900 uppercase tracking-wider text-center block">${partner.name}</span>`;
            }} data-id="element-423" />
                </div>)}
          </div>
        </div>
      </section>
    </>;
}